import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { api, type ApiUser } from '../api';
import { tokenStorage } from './storage';

interface AuthState {
  user: ApiUser | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  loginWithOtp: (email: string, code: string) => Promise<void>;
  adoptSession: (token: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<ApiUser | null>(null);
  const [loading, setLoading] = useState(true);

  // On load, if we have a token, resolve the current operator.
  useEffect(() => {
    if (!tokenStorage.get()) {
      setLoading(false);
      return;
    }
    api
      .me()
      .then(setUser)
      .catch(() => tokenStorage.clear())
      .finally(() => setLoading(false));
  }, []);

  async function login(email: string, password: string) {
    const res = await api.login(email, password);
    tokenStorage.set(res.token);
    setUser(res.user);
  }

  async function loginWithOtp(email: string, code: string) {
    const res = await api.otpVerify(email, code);
    tokenStorage.set(res.token);
    setUser(res.user);
  }

  // Adopt a session minted server-side (OAuth callback) — store the token, resolve the operator.
  async function adoptSession(token: string) {
    tokenStorage.set(token);
    const u = await api.me();
    setUser(u);
  }

  async function logout() {
    try {
      await api.logout();
    } finally {
      tokenStorage.clear();
      setUser(null);
    }
  }

  return (
    <AuthContext.Provider value={{ user, loading, login, loginWithOtp, adoptSession, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
